import {
  ButtonSetting,
  TableWidgetContainer,
} from '@/components/business/crud/widgets/TableWidgetContainer';
import { app } from '@/models/App';
import { PageList, PageListConfig } from '@/models/PageList';
import { ModelWidgetSchema } from '@/components/business/crud/interface/ModelWidgetSchema';
import { serviceManager } from '@/components/business/crud/service/ServiceManager';
import { computed, reactive, ref, watch, watchEffect } from 'vue';
import _ from 'lodash';
import { crudPath, request } from '@/util/request';
import { windows } from '@/components/common/window/Windows';
import { message, Modal } from 'ant-design-vue';

export function createCrudTableService(
  moduleName: string,
  controllerName: string
) {
  const Index = serviceManager.getService(
    moduleName,
    controllerName,
    'table',
    CrudTableService
  );
  const index = new Index(moduleName, controllerName);
  return index;
}

export class CrudTableService {
  protected readonly schema: ModelWidgetSchema;

  protected list: PageList;

  public readonly widgetContainer: TableWidgetContainer =
    new TableWidgetContainer('show', 'table');

  public readonly conditionButtons = ref<ButtonSetting[]>([]);

  public readonly listConfig: PageListConfig<unknown> & {
    addLoad?: any[];
  };

  constructor(protected moduleName: string, protected controllerName: string) {
    this.schema = app.data.widgetMap[moduleName][controllerName];
    if (this.schema.update) {
      this.widgetContainer.tableSetting.columnEdit = this.columnEdit.bind(this);
    }
    this.listConfig = reactive({
      pageNum: 1,
      pageSize: 50,
      path: crudPath(this.moduleName, this.controllerName, 'index'),
    });
    this.list = new PageList(this.listConfig);
    this.listConfig.orderBy = this.schema.orderBy;
    Object.entries(this.schema.widgets).map(([propName, widget]) => {
      this.widgetContainer.addField(propName, widget.title, widget.type, {
        groupName: widget.groupName,
        desc: widget.desc,
        sort: widget.sort ? 'none' : 'disabled',
        statusSettings: { show: widget.index },
      });
    });
    this.addSchemaButtons();
  }

  protected async columnEdit(row: any, propName: string, value: any) {
    const res = await request(crudPath(this.moduleName, this.controllerName, 'update'), {
      [this.schema.primary]: row[this.schema.primary],
      data: {
        [propName]: value,
      },
    });
    if (res) {
      await this.list.replaceData(item => {
        if (item[this.schema.primary] === row[this.schema.primary]) {
          return res;
        }
      })
      return true;
    }
    return false;
  }

  use() {
    return {
      schema: this.schema,
      listData: this.list.data,
      columns: this.widgetContainer.columns,
      rowSelection: this.rowSelection,
      buttons: this.buttons,
      onChange: this.onChange.bind(this),
    };
  }

  protected buttons = computed(() => {
    return [
      ...this.widgetContainer.buttons.value,
      ...this.conditionButtons.value.map((button) => ({
        ...button,
        handler: () => button.handler(this.listConfig.condition),
        confirm:
          typeof button.confirm === 'function'
            ? button.confirm(this.listConfig.condition)
            : button.confirm,
      })),
    ];
  });

  onChange(pagination: any, filters: any, sorter: any) {
    Object.keys(this.widgetContainer.fields.value).map((id) => {
      if (this.widgetContainer.fields.value[id].extra.sort !== 'disabled') {
        this.widgetContainer.fields.value[id].extra.sort = 'none';
      }
    });
    if (sorter.order) {
      this.widgetContainer.fields.value[sorter.field].extra.sort =
        sorter.order === 'ascend' ? 'asc' : 'desc';

    }
  }

  protected rowSelection = computed(() => {
    if (
      this.widgetContainer.buttons.value.find(
        (button) => button.type === 'select'
      )
    ) {
      return {
        fixed: true,
        selectedRowKeys: computed(() =>
          this.widgetContainer.tableSetting.selectedRows.map(
            (i) => i[this.schema.primary]
          )
        ),
        onChange: (rowKeys: any[], rows: any[]) => {
          this.widgetContainer.tableSetting.selectedRows = rows
        },
      };
    }
    return undefined;
  });

  /**
   * 初始化
   */
  async init() {
    // 翻页时清空选择行
    watch(
      () => this.list.data.pageNum,
      () => {
        this.widgetContainer.tableSetting.selectedRows = [];
      },
      { immediate: true }
    );
    this.bindOrderBy();
    // 获取数据
    this.listConfig.load = this.getLoadProps(this.listConfig.addLoad);
    this.listConfig.parser = async (origin) => {
      const widgetItemMap = this.widgetContainer.widgetItemMap;
      const morphList: {
        [indexPath: string]: {
          ids: { [id: string]: { name: string; index: number }[] };
          labels: string[];
        };
      } = {};
      Object.entries(widgetItemMap.value).map(([name, i]) => {
        const widget = i.props.widget;
        switch (widget.name) {
          case 'morph':
            origin.map((row, index) => {
              if (!row[name]) {
                return;
              }
              const modelName = row[widget.modelNameProp];
              if (!modelName) {
                return;
              }
              const model = widget.models[modelName];
              if (!model) {
                return;
              }
              if (!morphList[model.basePath]) {
                morphList[model.basePath] = { ids: {}, labels: [] };
              }
              const label = model.labelPropName || 'name';
              morphList[model.basePath].labels.push(label);
              if (!morphList[model.basePath].ids[row[name]]) {
                morphList[model.basePath].ids[row[name]] = [];
              }
              morphList[model.basePath].ids[row[name]].push({ name, index });
            });
            break;
        }
      });
      const tasks = Object.entries(morphList).map(async ([path, item]) => {
        const res = await request(path + '/index', {
          condition: { id: { $in: Object.keys(item.ids) } },
          load: _.uniq(['id'].concat(item.labels)),
          limit: Object.keys(item.ids).length,
          offset: 0,
        });
        Object.entries(item.ids).map(([id, rows]) => {
          const data = res.list.find((i: any) => i.id === id);
          rows.map((index) => {
            origin[index.index][index.name] = data;
          });
        });
      });
      await Promise.all(tasks);
      return origin;
    };
    await this.list.init();
  }

  /**
   * 绑定listConfig.orderBy和field.extra.sort属性
   * @protected
   */
  protected bindOrderBy() {
    watchEffect(() => {
      const map: Record<string, 'asc' | 'desc'> = {};
      this.listConfig.orderBy?.map((i) => {
        map[i.column] = i.order;
      });
      Object.values(this.widgetContainer.fields.value).map((field) => {
        if (map[field.propName] && field.extra.sort !== 'disabled') {
          field.extra.sort = map[field.propName];
        }
      });
    });
    watchEffect(() => {
      const orderBy: { column: string; order: 'asc' | 'desc' }[] = [];
      Object.values(this.widgetContainer.fields.value).map((field) => {
        if (field.extra.sort === 'asc' || field.extra.sort === 'desc') {
          orderBy.push({
            column: field.propName,
            order: field.extra.sort,
          });
        }
      });
      this.listConfig.orderBy = orderBy;
    });
  }

  protected addSchemaButtons() {
    this.widgetContainer.addButton('refresh', {
      type: 'table',
      icon: 'ReloadOutlined',
      text: '刷新',
      handler: async () => {
        await this.refreshList();
      },
    });
    if (this.schema.create) {
      this.widgetContainer.addButton('create', {
        type: 'table',
        icon: 'PlusOutlined',
        text: '新增',

        handler: async () => {
          const id = windows.open({
            content: {
              type: 'route',
              href: this.path('create'),
            },
            title: '新增' + this.schema.title,
          });
          const res = await windows.getCloseData(id);
          if (res) {
            await this.refreshList();
          }
        },
      });
    }
    if (this.schema.update) {
      this.widgetContainer.addButton('edit', {
        type: 'row',
        text: '编辑',
        handler: async (row: any) => {
          const id = windows.open({
            content: {
              type: 'route',
              href: this.path(
                'update?' + this.schema.primary + '=' + row[this.schema.primary]
              ),
            },
            title: '编辑' + this.schema.title,
          });
          windows.getCloseData(id).then(async (res) => {
            if (res) {
              await this.refreshList();
            }
          });
        },
      });
    }
    // 有隐藏属性时
    if (this.schema.view) {
      this.widgetContainer.addButton('view', {
        type: 'row',
        text: '详情',
        handler: async (row: any) => {
          const id = windows.open({
            content: {
              type: 'route',
              href: this.path(
                'view?' + this.schema.primary + '=' + row[this.schema.primary]
              ),
            },
            title: this.schema.title + '详情',
          });
          windows.getCloseData(id).then(async (res) => {
            if (res) {
              await this.refreshList();
            }
          });
        },
      });
    }
    if (this.schema.delete) {
      this.widgetContainer.addButton('delete', {
        type: 'row',
        text: '删除',
        confirm: '确定要删除此条记录',
        handler: async (rows: any) => {
          const res = await request(this.path('delete'), {
            condition: {
              [this.schema.primary]: rows[this.schema.primary],
            },
          });
          if (res) {
            message.success('删除成功');
            await this.refreshList();
          }
        },
      });
      this.widgetContainer.addButton('deleteSelect', {
        type: 'select',
        icon: 'DeleteOutlined',
        text: '删除',
        handler: async (rows: any) => {
          if (rows.length === 0) {
            message.info('未选中任何项');
            return;
          }
          Modal.confirm({
            title: '确定要删除这' + rows.length + '项吗？',
            onOk: async () => {
              const res = await request(this.path('delete'), {
                condition: {
                  [this.schema.primary]: {
                    $in: rows.map((row: any) => row[this.schema.primary]),
                  },
                },
              });
              if (res) {
                message.success('删除成功');
                await this.refreshList();
              }
            },
          });
        },
      });
    }
  }

  protected getLoadProps(loadProps: any[] = []) {
    const widgetItemMap = this.widgetContainer.widgetItemMap;
    Object.values(widgetItemMap.value).map((i) => {
      switch (i.props.widget.name) {
        case 'selectRemote':
          loadProps.push({
            propName: i.props.propName,
            load: [
              i.props.widget.labelFieldName,
              i.props.widget.valueFieldName,
            ],
          });
          break;
        case 'hasMany':
          break;
        default:
          loadProps.push(i.props.propName);
      }
    });
    _.uniqWith(loadProps, (a, b) => _.isEqual(a, b));
    return loadProps;
  }

  public async refreshList() {
    await this.list.refresh();
  }

  protected path(action: string) {
    return crudPath(this.moduleName, this.controllerName, action);
  }
}
